import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { motion } from "framer-motion";
import { Check, ArrowUpRight, Loader2, Landmark, TrendingUp, Briefcase, Building2, Crown, Sparkles, X } from "lucide-react";
import { toast } from "sonner";

const SUITE_ICON = { enterprise: Building2, advisory: Briefcase, investor: TrendingUp, government: Landmark };
const SUITE_COLOR = { enterprise: "#22C55E", advisory: "#A855F7", investor: "#D4AF37", government: "#3B82F6" };

const fmtGhs = (n) => {
  if (n == null) return "—";
  if (n >= 1_000_000) return `GH₵ ${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1000) return `GH₵ ${(n / 1000).toFixed(0)}K`;
  return `GH₵ ${n}`;
};

export default function Pricing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [cycle, setCycle] = useState("annual");
  const [busy, setBusy] = useState("");
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [quote, setQuote] = useState({ plan_id: "government_suite", deployment: "cloud" });
  const [setupOpen, setSetupOpen] = useState(null); // holds the plan being configured

  useEffect(() => {
    api.get("/billing/plans").then((r) => setPlans(r.data.plans));
  }, []);

  const openSubscribe = (plan_id) => {
    if (!user) {
      toast.message("Please sign in first");
      navigate("/login"); return;
    }
    const plan = plans.find((p) => p.id === plan_id);
    if (!plan) return;
    setSetupOpen(plan);
  };

  const confirmSubscribe = async (planId, setupTier) => {
    setBusy(planId);
    setSetupOpen(null);
    try {
      const r = await api.post("/billing/checkout", {
        plan_id: planId, billing_cycle: cycle,
        origin_url: window.location.origin,
        setup_tier: setupTier,
      });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
      setBusy("");
    }
  };

  const startTrial = async (plan_id) => {
    if (!user) { navigate("/login"); return; }
    setBusy(plan_id + "-trial");
    try {
      await api.post("/billing/start-trial", { plan_id });
      toast.success("14-day trial activated");
      navigate("/billing");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start trial");
    } finally { setBusy(""); }
  };

  const submitQuote = async () => {
    if (!user) { navigate("/login"); return; }
    if (!quote.organization || !quote.notes) {
      toast.error("Organization name and requirements required");
      return;
    }
    try {
      await api.post("/billing/quote", quote);
      toast.success("Quote request submitted. Our institutional licensing team will reach out within 48h.");
      setQuoteOpen(false); setQuote({ plan_id: "government_suite", deployment: "cloud" });
    } catch (e) { toast.error("Failed"); }
  };

  const enterprisePlans = plans.filter((p) => p.suite === "enterprise");
  const advisoryPlans = plans.filter((p) => p.suite === "advisory");
  const investorPlans = plans.filter((p) => p.suite === "investor");
  const govPlans = plans.filter((p) => p.suite === "government");

  return (
    <div className="min-h-screen" style={{ background: "var(--bg-app)" }} data-testid="pricing-page">
      {/* Header */}
      <header className="border-b border-white/10 nexus-glass px-8 py-4 flex items-center justify-between sticky top-0 z-30">
        <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate("/")} data-testid="pricing-logo">
          <img
            src="/pandora-nexus-mark.png"
            alt="Pandora Nexus"
            className="w-9 h-9 object-contain"
            style={{ filter: "drop-shadow(0 0 10px rgba(212,175,55,0.35))" }}
          />
          <div>
            <div className="font-display text-lg text-white">
              Pandora<span style={{ color: "var(--gold-500)" }}>Nexus</span>
            </div>
            <div className="label-eyebrow mt-0.5">Total Institutional Intelligence</div>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <Link to="/about" data-testid="pricing-about-link" className="hidden md:inline-block px-3 py-2 text-[11px] font-mono tracking-widest text-slate-300 hover:text-[#D4AF37]">
            ABOUT
          </Link>
          {user ? (
            <button onClick={() => navigate("/")} className="px-4 py-2 text-sm font-mono tracking-widest text-slate-300 hover:text-white">
              COMMAND CENTER →
            </button>
          ) : (
            <button onClick={() => navigate("/login")} className="px-4 py-2 text-sm font-mono tracking-widest text-slate-300 hover:text-[#D4AF37]">
              SIGN IN →
            </button>
          )}
        </div>
      </header>

      <main className="px-8 py-16 max-w-7xl mx-auto">
        {/* Hero */}
        <motion.section initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-16">
          <div className="label-eyebrow mb-4">// Institutional Licensing</div>
          <h1 className="font-display text-5xl lg:text-6xl text-white tracking-tight max-w-4xl mx-auto leading-tight">
            <span style={{ color: "var(--gold-500)" }}>AI-powered institutional</span> intelligence & operational infrastructure.
          </h1>
          <p className="text-slate-400 mt-6 max-w-2xl mx-auto leading-relaxed">
            Pandora Nexus is licensed by suite, not seat-count alone — tailored to enterprises, advisory firms, investors,
            and governments. Annual contracts with onboarding included.
          </p>

          {/* Cycle toggle */}
          <div className="mt-10 inline-flex border border-white/10">
            {[
              { id: "monthly", label: "Monthly" },
              { id: "annual", label: "Annual · Save up to 20%" },
            ].map((c) => (
              <button
                key={c.id}
                data-testid={`cycle-${c.id}`}
                onClick={() => setCycle(c.id)}
                className={`px-6 py-2.5 text-sm font-mono tracking-widest ${cycle === c.id ? "bg-[#D4AF37] text-[#050B14]" : "text-slate-400 hover:text-white"}`}
              >{c.label.toUpperCase()}</button>
            ))}
          </div>

          {/* Payment methods strip */}
          <div className="mt-8 flex flex-wrap items-center justify-center gap-2 text-[10px] font-mono tracking-widest text-slate-500" data-testid="payment-methods">
            <span>ACCEPTED:</span>
            {["VISA", "MASTERCARD", "MTN MoMo", "VODAFONE CASH", "AIRTELTIGO", "BANK TRANSFER"].map((m) => (
              <span key={m} className="px-2.5 py-1 border border-white/10 text-slate-300">{m}</span>
            ))}
          </div>
          <div className="mt-2 text-[10px] font-mono tracking-widest text-slate-600 text-center">
            POWERED BY PAYSTACK · CHARGED IN GHANA CEDIS (GHS)
          </div>
        </motion.section>

        {/* Enterprise tier */}
        <PricingGroup
          title="Pandora Enterprise Suite"
          subtitle="For SMEs, corporate organizations, and growing institutions"
          plans={enterprisePlans} cycle={cycle} onSubscribe={openSubscribe} onTrial={startTrial} busy={busy}
        />

        {/* Advisory */}
        <PricingGroup
          title="Pandora Advisory Suite"
          subtitle="For consulting firms & professional advisory practices"
          plans={advisoryPlans} cycle={cycle} onSubscribe={openSubscribe} onTrial={startTrial} busy={busy}
        />

        {/* Investor */}
        <PricingGroup
          title="Pandora Investor Suite"
          subtitle="For investors, DFIs, capital firms and funds"
          plans={investorPlans} cycle={cycle} onSubscribe={openSubscribe} onTrial={startTrial} busy={busy}
        />

        {/* Government */}
        <section className="mb-20" data-testid="group-government">
          <div className="mb-8">
            <div className="label-eyebrow mb-2">// Pandora Government Suite</div>
            <h2 className="font-display text-3xl text-white">Contract-Based Institutional Licensing</h2>
            <p className="text-slate-400 mt-2 max-w-2xl text-sm">
              For ministries, agencies, national platforms and PPP frameworks. From GH₵ 400K – GH₵ 16M+ per year.
            </p>
          </div>
          {govPlans.map((p) => (
            <div key={p.id} className="nexus-card p-8" data-testid={`plan-${p.id}`}>
              <div className="flex items-start justify-between flex-wrap gap-6">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-3">
                    <Landmark className="w-5 h-5" style={{ color: SUITE_COLOR.government }} strokeWidth={1.5} />
                    <span className="label-eyebrow">Government · Contract</span>
                  </div>
                  <h3 className="font-display text-2xl text-white">{p.name}</h3>
                  <p className="text-sm text-slate-400 mt-2">{p.audience}</p>
                  <div className="mt-4 font-mono text-xs text-slate-400">
                    Setup from <span className="text-[#D4AF37]">GH₵ {p.setup_fee_min.toLocaleString()}</span>
                    {" · "}Annual contracts from <span className="text-[#D4AF37]">GH₵ 400K – GH₵ 16M+</span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 mt-6">
                    {p.highlights.map((h) => (
                      <div key={h} className="flex items-center gap-2 text-sm text-slate-300">
                        <Check className="w-3.5 h-3.5" style={{ color: "var(--gold-400)" }} strokeWidth={2} />
                        <span>{h}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <button
                  onClick={() => { setQuote({ ...quote, plan_id: p.id }); setQuoteOpen(true); }}
                  data-testid="request-quote-btn"
                  className="px-6 py-3 font-medium text-sm flex items-center gap-2"
                  style={{ background: "var(--gold-500)", color: "#050B14" }}
                >
                  Request Institutional Quote <ArrowUpRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </section>

        {/* Add-ons */}
        <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-20">
          {[
            ["Setup & Onboarding", "GH₵ 16K – GH₵ 8M+", "Implementation, configuration, integration, and executive training."],
            ["AI Usage Overage", "Metered", "Beyond plan quota — billed per 1,000 institutional AI generations."],
            ["Additional User Seats", "Per seat", "Scale your team beyond plan defaults at institutional rates."],
            ["White Labeling", "GH₵ 400K – GH₵ 4M+", "Branded deployments, private cloud, custom workflows."],
          ].map(([t, p, d]) => (
            <div key={t} className="nexus-card p-5">
              <div className="label-eyebrow">// Add-on</div>
              <div className="font-display text-base text-white mt-2">{t}</div>
              <div className="font-mono text-[#D4AF37] text-sm mt-1">{p}</div>
              <div className="text-xs text-slate-400 mt-2 leading-relaxed">{d}</div>
            </div>
          ))}
        </section>

        {/* Positioning */}
        <section className="border border-[#D4AF37]/30 bg-[#0A111F] p-12 text-center" data-testid="positioning-section">
          <Sparkles className="w-6 h-6 mx-auto mb-4" style={{ color: "var(--gold-400)" }} strokeWidth={1.5} />
          <h2 className="font-display text-3xl text-white max-w-3xl mx-auto leading-tight">
            We do not sell software. We license{" "}
            <span style={{ color: "var(--gold-500)" }}>institutional intelligence infrastructure</span>.
          </h2>
          <p className="text-slate-400 mt-4 max-w-2xl mx-auto text-sm">
            Annual contracts. Institutional licensing. Government infrastructure agreements. White-label deployments.
          </p>
        </section>
      </main>

      {/* Setup fee configurator */}
      {setupOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70" onClick={() => setSetupOpen(null)}>
          <motion.div
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
            className="bg-[#0A111F] border border-white/10 p-8 w-full max-w-2xl"
            onClick={(e) => e.stopPropagation()}
            data-testid="setup-modal"
          >
            <div className="label-eyebrow mb-1">// Configure Onboarding</div>
            <h2 className="font-display text-2xl text-white mb-2">{setupOpen.name}</h2>
            <p className="text-slate-400 text-sm mb-6">
              Choose an onboarding tier. Pandora handles implementation, configuration, integration and executive training.
              Pay once with your first subscription.
            </p>
            <div className="space-y-3">
              {[
                { id: "none", label: "Skip onboarding", amount: 0, sub: "Self-serve setup. You configure everything." },
                { id: "light", label: "Light onboarding", amount: setupOpen.setup_fee_min, sub: "1-week implementation. Basic configuration + 2hr training." },
                { id: "standard", label: "Standard onboarding", amount: Math.round(((setupOpen.setup_fee_min || 0) + (setupOpen.setup_fee_max || 0)) / 2), sub: "3-week implementation. Full configuration + workflow design + team training.", recommended: true },
                { id: "premium", label: "Premium onboarding", amount: setupOpen.setup_fee_max, sub: "6-week white-glove implementation. Integrations + executive workshops + change management." },
              ].map((opt) => {
                const subTotal = cycle === "annual" ? setupOpen.annual_ghs : setupOpen.monthly_ghs;
                const total = (subTotal || 0) + (opt.amount || 0);
                return (
                  <button
                    key={opt.id}
                    onClick={() => confirmSubscribe(setupOpen.id, opt.id)}
                    data-testid={`setup-tier-${opt.id}`}
                    className="w-full text-left p-4 border border-white/10 hover:border-[#D4AF37] transition-colors group"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <div className="text-white font-medium">{opt.label}</div>
                          {opt.recommended && (
                            <span className="px-1.5 py-0.5 font-mono text-[9px] tracking-widest" style={{ background: "var(--gold-500)", color: "#050B14" }}>RECOMMENDED</span>
                          )}
                        </div>
                        <div className="text-xs text-slate-500 mt-1">{opt.sub}</div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="font-mono text-lg text-white">
                          {opt.amount > 0 ? `+ GH₵ ${opt.amount.toLocaleString()}` : "FREE"}
                        </div>
                        <div className="font-mono text-[10px] text-slate-500 tracking-widest mt-0.5">
                          TOTAL GH₵ {total.toLocaleString()}
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
            <div className="mt-5 text-[11px] text-slate-500 leading-relaxed text-center">
              You'll be redirected to Paystack to complete payment. Charges include onboarding + first {cycle === "annual" ? "year" : "month"}.
            </div>
            <button onClick={() => setSetupOpen(null)} className="absolute top-4 right-4 text-slate-500 hover:text-white">
              <X className="w-4 h-4" />
            </button>
          </motion.div>
        </div>
      )}

      {/* Quote modal */}
      {quoteOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70" onClick={() => setQuoteOpen(false)}>
          <motion.div
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
            className="bg-[#0A111F] border border-white/10 p-8 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="quote-modal"
          >
            <div className="label-eyebrow mb-1">// Institutional Licensing Inquiry</div>
            <h2 className="font-display text-2xl text-white mb-6">Request a Quote</h2>
            <div className="space-y-4">
              <Field label="Organization name *">
                <input data-testid="quote-org" value={quote.organization || ""} onChange={(e) => setQuote({ ...quote, organization: e.target.value })} className="pr-input" />
              </Field>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Country">
                  <input value={quote.country || ""} onChange={(e) => setQuote({ ...quote, country: e.target.value })} className="pr-input" />
                </Field>
                <Field label="Audience">
                  <select value={quote.audience || ""} onChange={(e) => setQuote({ ...quote, audience: e.target.value })} className="pr-input">
                    <option value="">— select —</option>
                    <option value="ministry">Ministry / Federal Agency</option>
                    <option value="state">State / Regional Government</option>
                    <option value="dfi">DFI / Multilateral</option>
                    <option value="sovereign">Sovereign / National Project</option>
                    <option value="enterprise">Large Enterprise</option>
                  </select>
                </Field>
                <Field label="Estimated seats">
                  <input type="number" value={quote.seats_estimate || ""} onChange={(e) => setQuote({ ...quote, seats_estimate: +e.target.value })} className="pr-input" />
                </Field>
                <Field label="Deployment preference">
                  <select value={quote.deployment} onChange={(e) => setQuote({ ...quote, deployment: e.target.value })} className="pr-input">
                    <option value="cloud">Pandora-hosted (cloud)</option>
                    <option value="private_cloud">Private cloud</option>
                    <option value="on_prem">On-premises</option>
                    <option value="hybrid">Hybrid</option>
                  </select>
                </Field>
                <Field label="Timeline">
                  <input value={quote.timeline || ""} onChange={(e) => setQuote({ ...quote, timeline: e.target.value })} className="pr-input" placeholder="e.g., Q3 2026" />
                </Field>
                <Field label="Budget range (GH₵)">
                  <input value={quote.budget_range || ""} onChange={(e) => setQuote({ ...quote, budget_range: e.target.value })} className="pr-input" placeholder="e.g., GH₵ 4M – GH₵ 16M" />
                </Field>
              </div>
              <Field label="Requirements / Notes *">
                <textarea data-testid="quote-notes" rows={4} value={quote.notes || ""} onChange={(e) => setQuote({ ...quote, notes: e.target.value })} className="pr-input resize-none" placeholder="Describe your institutional context, departments, integrations, etc." />
              </Field>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button onClick={() => setQuoteOpen(false)} className="px-5 py-2 border border-white/10 text-slate-300">Cancel</button>
              <button onClick={submitQuote} data-testid="quote-submit" className="px-5 py-2 font-medium" style={{ background: "var(--gold-500)", color: "#050B14" }}>Submit Inquiry</button>
            </div>
          </motion.div>
        </div>
      )}
      <style>{`.pr-input { width:100%; padding:0.5rem 0.75rem; background:#050B14; border:1px solid rgba(255,255,255,0.1); color:#F8FAFC; font-family:'IBM Plex Mono', monospace; font-size:0.875rem; outline:none; } .pr-input:focus { border-color:#D4AF37; }`}</style>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <label className="label-eyebrow block mb-1.5">{label}</label>
    {children}
  </div>
);

const PricingGroup = ({ title, subtitle, plans, cycle, onSubscribe, onTrial, busy }) => (
  <section className="mb-16" data-testid={`group-${plans[0]?.suite || ''}`}>
    <div className="mb-8">
      <div className="label-eyebrow mb-2">// {title}</div>
      <h2 className="font-display text-3xl text-white">{title}</h2>
      <p className="text-slate-400 mt-2 max-w-2xl text-sm">{subtitle}</p>
    </div>
    <div className={`grid gap-4 ${plans.length === 3 ? "grid-cols-1 md:grid-cols-3" : "grid-cols-1 md:grid-cols-2 lg:grid-cols-3"}`}>
      {plans.map((p) => {
        const Icon = SUITE_ICON[p.suite] || Crown;
        const price = cycle === "annual" ? p.annual_ghs : p.monthly_ghs;
        return (
          <motion.div
            key={p.id}
            whileHover={{ y: -4 }}
            className={`nexus-card p-7 relative ${p.popular ? "border-[#D4AF37]/60" : ""}`}
            data-testid={`plan-${p.id}`}
          >
            {p.popular && (
              <div className="absolute -top-3 left-7 px-3 py-0.5 font-mono text-[10px] tracking-widest" style={{ background: "var(--gold-500)", color: "#050B14" }}>
                MOST POPULAR
              </div>
            )}
            <div className="flex items-center justify-between mb-4">
              <Icon className="w-5 h-5" style={{ color: SUITE_COLOR[p.suite] }} strokeWidth={1.5} />
              <span className="font-mono text-[10px] text-slate-500 tracking-widest">TIER {p.tier}</span>
            </div>
            <div className="font-display text-xl text-white">{p.name}</div>
            <div className="text-xs text-slate-500 mt-1.5 min-h-[32px]">{p.audience}</div>

            <div className="mt-5 pb-5 border-b border-white/5">
              <div className="font-mono text-4xl text-white">{fmtGhs(price)}</div>
              <div className="text-xs text-slate-500 font-mono mt-1">
                {cycle === "annual" ? "PER YEAR" : "PER MONTH"} · GHS
              </div>
              {p.setup_fee_min && (
                <div className="text-[11px] text-slate-500 mt-2">
                  + Setup GH₵ {p.setup_fee_min.toLocaleString()}–GH₵ {p.setup_fee_max.toLocaleString()}
                </div>
              )}
            </div>

            <ul className="mt-5 space-y-2 min-h-[140px]">
              {p.highlights.map((h) => (
                <li key={h} className="flex items-start gap-2 text-sm text-slate-300">
                  <Check className="w-3.5 h-3.5 mt-0.5 shrink-0" style={{ color: "var(--gold-400)" }} strokeWidth={2.5} />
                  <span>{h}</span>
                </li>
              ))}
            </ul>

            <div className="mt-6 space-y-2">
              <button
                onClick={() => onSubscribe(p.id)}
                disabled={busy === p.id}
                data-testid={`subscribe-${p.id}`}
                className="w-full py-2.5 font-medium text-sm flex items-center justify-center gap-2 disabled:opacity-60"
                style={{ background: "var(--gold-500)", color: "#050B14" }}
              >
                {busy === p.id ? <Loader2 className="w-4 h-4 animate-spin" /> : "Subscribe"}
              </button>
              <button
                onClick={() => onTrial(p.id)}
                disabled={busy === p.id + "-trial"}
                data-testid={`trial-${p.id}`}
                className="w-full py-2.5 font-medium text-sm border border-white/10 text-slate-300 hover:border-[#D4AF37]/50 hover:text-[#D4AF37] disabled:opacity-60"
              >
                Start 14-day trial
              </button>
            </div>
          </motion.div>
        );
      })}
    </div>
  </section>
);
